import { Injectable } from '@angular/core';
import { SicorApiService } from '../SicorService/sicor-api.service';
import { AppUserHomeRequest } from '../SicorService/RequestObject/AppUserHomeRequest';
import { InstallationData } from '../../models/InstallationData';
import { take, tap } from 'rxjs/operators';
import { GlobalVariablesService } from '../global-variables.service';
import { StorageService } from './storage.service';
import { GlobalMethodsService } from '../global-methods.service';
import { NavigationService } from './navigation.service';
import { TranslateConfigService } from 'src/app/services/commons/translate-config.service';
import { ContactUser } from '../../models/ContactUser';
import { Instruction } from '../../models/Instruction';
import { AppUserTextRequest } from '../SicorService/RequestObject/AppUserTextRequest';
import { DocsListRequest } from '../SicorService/RequestObject/DocsListRequest';
import { Document } from 'src/app/models/Document';
import { User } from 'src/app/models/User';
import { UserUpdRequest } from '../SicorService/RequestObject/UserUpdRequest';
import { UserQrRequest } from '../SicorService/RequestObject/UserQrRequest';
import { AppUserDeviceConfiguredRequest } from '../SicorService/RequestObject/AppUserDeviceConfiguredRequest';
import { ContactRequest } from '../SicorService/RequestObject/ContactRequest';
import { AppUserDeviceListRequest } from '../SicorService/RequestObject/AppUserDeviceListRequest';
import { Device } from '../../models/Device';
import { BillingInfo } from '../../models/BillingInfo';
import { CommandConnectorQueueInsRequest } from '../SicorService/RequestObject/CommandConnectorQueueInsRequest';
import { AppuserZoneUpdRequest } from '../SicorService/RequestObject/AppuserZoneUpdRequest';
import { ModalController } from '@ionic/angular';
import { ContactChangeRequest } from '../SicorService/RequestObject/ContactChangeRequest';
import { InstallationNameChangeRequest } from '../SicorService/RequestObject/InstallationNameChangeRequest';
import { firstValueFrom } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class ProfileInstallationService {

  constructor(public gvs: GlobalVariablesService,
    private sicorAPI: SicorApiService,
    private translate: TranslateConfigService,
    public gms: GlobalMethodsService,
    private modalCtrl: ModalController,
    private navigation: NavigationService,) { }

  public async getInstallationData(user: string, s_ins: number): Promise<InstallationData> {
    const appUserHomeRequest: AppUserHomeRequest = new AppUserHomeRequest(s_ins, user);
    let installationData: InstallationData = new InstallationData();
    return new Promise(async (resolve, reject) => {
      await firstValueFrom((await this.sicorAPI.requestInstallationInfo(appUserHomeRequest)).pipe(
        take(1),
        tap({
          next: async res => {
            if (res.resultSet[0] != null) {
              if (res.resultSet[0].error != null && res.resultSet[0].error === 401) {
                resolve(null);
                await this.navigation.logout();
                await this.gms.openGenericModal(await this.translate.getTranslate('MODAL.titleError'), await this.translate.getTranslate('MESSAGES.resourceNotAvailable'),
                  'modal-default', await this.translate.getTranslate('BUTTON.agreed'));
              }
              else {
                installationData.city = res.resultSet[0].city;
                installationData.fname = res.resultSet[0].fname;
                installationData.name = res.resultSet[0].name;
                installationData.phone = res.resultSet[0].phone;
                installationData.phone2 = res.resultSet[0].phone2;
                installationData.state = res.resultSet[0].state;
                installationData.street1 = res.resultSet[0].street1;
                installationData.zip = res.resultSet[0].zip;
                installationData.e_mail1 = res.resultSet[0].e_mail1;
                installationData.e_mail2 = res.resultSet[0].e_mail2;
                installationData.panel = res.resultSet[0].panel;
                installationData.alias = res.resultSet[0].alias_name;
                this.gvs.infInstallation = installationData;
                resolve(installationData);
              }
            } else {
              resolve(null);
            }
          }, error: async e => {
            resolve(null);
            await this.gms.openFailedProcessModal();
            await this.navigation.goHome();
          }
        })
      ));
    });
  }

  public async getBillingInfo(user: string, s_ins: number): Promise<BillingInfo> {
    const appUserHomeRequest: AppUserHomeRequest = new AppUserHomeRequest(s_ins, user);
    let billingInfo: BillingInfo = new BillingInfo();
    return new Promise(async (resolve, reject) => {
      await firstValueFrom((await this.sicorAPI.requestBillingInfo(appUserHomeRequest)).pipe(
        take(1),
        tap({
          next: async res => {
            if (res.resultSet[0] != null) {
              if (res.resultSet[0].error != null && res.resultSet[0].error === 401) {
                resolve(null);
                await this.navigation.logout();
                await this.gms.openGenericModal(await this.translate.getTranslate('MODAL.titleError'), await this.translate.getTranslate('MESSAGES.resourceNotAvailable'),
                  'modal-default', await this.translate.getTranslate('BUTTON.agreed'));
              }
              else {
                billingInfo.city = res.resultSet[0].city;
                billingInfo.fname = res.resultSet[0].fname;
                billingInfo.name = res.resultSet[0].name;
                billingInfo.phone1 = res.resultSet[0].phone;
                billingInfo.phone2 = res.resultSet[0].phone2;
                billingInfo.state = res.resultSet[0].state;
                billingInfo.street1 = res.resultSet[0].street1;
                billingInfo.zip = res.resultSet[0].zip;
                billingInfo.email1 = res.resultSet[0].e_mail1;
                billingInfo.email2 = res.resultSet[0].e_mail2;
                this.gvs.billingInfo = billingInfo;
                resolve(billingInfo);
              }
            } else {
              resolve(null);
            }
          }, error: async e => {
            resolve(null);
            await this.gms.openFailedProcessModal();
            await this.navigation.goHome();
          }
        })
      ));
    });
  }

  public async getContactsUser(user: string, s_ins: number, pix: number): Promise<ContactUser[]> {
    const appUserHomeRequest: ContactRequest = new ContactRequest(s_ins, user, pix);
    let contactList: ContactUser[] = [];
    return new Promise(async (resolve, reject) => {
      await firstValueFrom((await this.sicorAPI.requestContactUsers(appUserHomeRequest)).pipe(
        take(1),
        tap({
          next: async res => {
            if (res.resultSet != null && res.resultSet[0] != null) {
              if (res.resultSet[0].error != null && res.resultSet[0].error === 401) {
                this.modalCtrl.dismiss(false, '', 'protocolModal');
                resolve(undefined);
                await this.navigation.logout();
                await this.gms.openGenericModal(await this.translate.getTranslate('MODAL.titleError'), await this.translate.getTranslate('MESSAGES.resourceNotAvailable'),
                  'modal-default', await this.translate.getTranslate('BUTTON.agreed'));
              }
              else {
                if (res.resultSet[0].error != 0) {
                  contactList = res.resultSet;
                  this.gvs.contactsUserInstallation = contactList;
                  resolve(contactList);
                }
              }
            } else {
              resolve(null);
            }
          }, error: async e => {
            resolve(null);
            this.gms.openFailedProcessModal();
            await this.navigation.goHome();
          }
        })
      ));
    });
  }

  public async getUsers(user: string, s_ins: number): Promise<User[]> {
    const appUserHomeRequest: AppUserHomeRequest = new AppUserHomeRequest(s_ins, user);
    let users: User[] = [];
    return new Promise(async (resolve, reject) => {
      await firstValueFrom((await this.sicorAPI.listUsersRequest(appUserHomeRequest)).pipe(
        take(1),
        tap({
          next: async res => {
            if (res.resultSet != null) {
              if (res.resultSet[0] != null && res.resultSet[0].error != null && res.resultSet[0].error === 401) {
                this.modalCtrl.dismiss(false, '', 'loadSpinner');
                resolve(undefined);
                await this.navigation.logout();
                await this.gms.openGenericModal(await this.translate.getTranslate('MODAL.titleError'), await this.translate.getTranslate('MESSAGES.resourceNotAvailable'),
                  'modal-default', await this.translate.getTranslate('BUTTON.agreed'));
              }
              else {
                users = res.resultSet;
                resolve(users);
              }
            }
          }, error: async e => {
            resolve(null);
            this.gms.openFailedProcessModal();
            await this.navigation.goHome();
          }
        })
      ));
    });
  }

  public async changeProfileData(paramsRequest: UserUpdRequest): Promise<number> {
    return new Promise(async (resolve, reject) => {
      await firstValueFrom((await this.sicorAPI.cAppuserUserUpdRequest(paramsRequest)).pipe(
        take(1),
        tap({
          next: async res => {
            if (res.resultSet[0] != null) {
              if(res.resultSet[0].s_cont != null && res.resultSet[0].cix != null){
                resolve(0);
              }else if(res.resultSet[0].error != null){
                if(res.resultSet[0].error === 401){
                  resolve(null);
                  await this.navigation.logout();
                  await this.gms.openGenericModal(await this.translate.getTranslate('MODAL.titleError'), await this.translate.getTranslate('MESSAGES.resourceNotAvailable'),
                    'modal-default', await this.translate.getTranslate('BUTTON.agreed'));
                }else{
                  resolve(res.resultSet[0].error);
                }
              }else{
                resolve(null);
                await this.gms.openFailedProcessModal();
                await this.navigation.goHome();
              }
            }else{
              resolve(null);
              await this.gms.openFailedProcessModal();
              await this.navigation.goHome();
            }
          }, error: async e => {
            resolve(null);
            await this.gms.openFailedProcessModal();
            await this.navigation.goHome();
          }
        })
      ));
    });
  }

  public async gAppuserText(user: string, lang: string, text_num: number, screen_id: string, screen_name: string): Promise<Instruction[]> {
    const appUserTextRequest: AppUserTextRequest = new AppUserTextRequest(user, lang, text_num, screen_id, screen_name);
    let instructions: Instruction[] = [];
    return new Promise(async (resolve, reject) => {
      await firstValueFrom((await this.sicorAPI.gAppuserTextRequest(appUserTextRequest)).pipe(
        take(1),
        tap({
          next: async res => {
            if (res.resultSet != null) {
              instructions = res.resultSet;
              resolve(instructions);
            }
          }, error: e => {
            this.gms.openFailedProcessModal();
            resolve(null);
          }
        })
      ));
    });
  }

  public async getUserManual(user: string, s_ins: number): Promise<Document> {
    const requestParams: DocsListRequest = new DocsListRequest(user, s_ins, "ALL");
    return new Promise(async (resolve, reject) => {
      await firstValueFrom((await this.sicorAPI.isAppUserDocsListRequest(requestParams)).pipe(
        take(1),
        tap({
          next: async res => {
            if (res.resultSet[0] != null) {
              let document: Document = new Document();
              document = res.resultSet[0];
              resolve(document);

            }
            else {
              resolve(null);
            }
          }, error: e => {
            resolve(null);
          }
        })
      ));
    });
  }

  public async cAppuserUserUpd(s_ins: number, user: User, opc: number, originScreen: string, creator: string): Promise<User> {
    const paramsRequest: UserUpdRequest = new UserUpdRequest(creator, s_ins, opc, user.name,
      user.phone, user.tag_ind, user.tag_num, user.key_code, user.user_on_keypad, user.appuser_ind,
      user.remctrl_ind, user.remctrl_num, user.panic_code, user.key_code_ind, user.panic_code_ind,
      'SIC', user.email, opc === 1 ? 0 : user.s_cont, opc === 1 ? 0 : user.cix);
    return new Promise(async (resolve, reject) => {
      await firstValueFrom((await this.sicorAPI.cAppuserUserUpdRequest(paramsRequest)).pipe(
        take(1),
        tap({
          next: async res => {
            if (res.resultSet[0] != null && res.resultSet[0].error != null && res.resultSet[0].error === 401) {
              if (originScreen != 'profileScreen') {
                this.modalCtrl.dismiss(false, '', 'loadSpinner');
              }
              switch (originScreen) {
                case 'panicCodeComponent':
                  this.modalCtrl.dismiss(false, '', 'panicCodeModal');
                  break;
                case 'codeComponent':
                  this.modalCtrl.dismiss(false, '', 'codeModal');
                  break;
                case 'newUserComponent':
                  this.modalCtrl.dismiss(false, '', 'newUserModal');
                  break;
                case 'commandComponent':
                  this.modalCtrl.dismiss(false, '', 'commandModal');
                  break;
                case 'keyComponent':
                  this.modalCtrl.dismiss(false, '', 'keyModal');
                  break;
                default:
                  break;
              }
              resolve(undefined);
              await this.navigation.logout();
              await this.gms.openGenericModal(await this.translate.getTranslate('MODAL.titleError'), await this.translate.getTranslate('MESSAGES.resourceNotAvailable'),
                'modal-default', await this.translate.getTranslate('BUTTON.agreed'));
            }
            if (res.resultSet[0] != null && res.resultSet[0].s_cont != null && res.resultSet[0].cix != null) {
              user.s_cont = res.resultSet[0].s_cont;
              user.cix = res.resultSet[0].cix;
              user.name = res.resultSet[0].name;
              user.email = res.resultSet[0].user;
              user.phone = res.resultSet[0].phone;
              resolve(user);
            } else {
              resolve(null);
            }
          }, error: e => {
            resolve(null);
          }
        })
      ));
    });
  }

  public async cAppuserUserQr(user: string, s_ins: number, s_cont: number, cix: number, qr_code: string, opc: number, originScreen: string): Promise<boolean> {
    const paramsRequest: UserQrRequest = new UserQrRequest(user, s_ins, s_cont, cix, qr_code, opc);
    return new Promise(async (resolve, reject) => {
      await firstValueFrom((await this.sicorAPI.cAppuserUserQrRequest(paramsRequest)).pipe(
        take(1),
        tap({
          next: async res => {
            if (res.returnCode != null && res.returnCode === 0) {
              if (res.resultSet[0] != null && res.resultSet[0].error != null && res.resultSet[0].error === 401) {
                if (originScreen === 'commandComponent') {
                  this.modalCtrl.dismiss(false, '', 'commandModal');
                }
                if (originScreen === 'keyComponent') {
                  this.modalCtrl.dismiss(false, '', 'keyModal');
                }
                resolve(undefined);
                await this.navigation.logout();
                await this.gms.openGenericModal(await this.translate.getTranslate('MODAL.titleError'), await this.translate.getTranslate('MESSAGES.resourceNotAvailable'),
                  'modal-default', await this.translate.getTranslate('BUTTON.agreed'));
              }
              else {
                resolve(true);
              }
            } else {
              resolve(false);
            }
          }, error: e => {
            resolve(false);
          }
        })
      ));
    });
  }

  public async gAppuserDeviceConfigured(user: string, s_ins: number, s_cont: number, cix: number, opc: number, originScreen: string): Promise<string> {
    const paramsRequest: AppUserDeviceConfiguredRequest = new AppUserDeviceConfiguredRequest(user, s_ins, s_cont, cix, opc);
    return new Promise(async (resolve, reject) => {
      await firstValueFrom((await this.sicorAPI.gAppuserDeviceConfiguredRequest(paramsRequest)).pipe(
        take(1),
        tap({
          next: async res => {
            if (res.debug != null && res.debug.Table[0] != null) {
              if (res.resultSet[0] != null && res.resultSet[0].error != null && res.resultSet[0].error === 401) {
                if (originScreen === 'commandComponent') {
                  this.modalCtrl.dismiss(false, '', 'commandModal');
                }
                if (originScreen === 'keyComponent') {
                  this.modalCtrl.dismiss(false, '', 'keyModal');
                }
                resolve(undefined);
                await this.navigation.logout();
                await this.gms.openGenericModal(await this.translate.getTranslate('MODAL.titleError'), await this.translate.getTranslate('MESSAGES.resourceNotAvailable'),
                  'modal-default', await this.translate.getTranslate('BUTTON.agreed'));
              }
              else {
                if (opc === 5) {
                  resolve(res.debug.Table[0].remctrl_num);
                } else {
                  resolve(res.debug.Table[0].tag_num);
                }
              }
            } else {
              resolve('');
            }
          }, error: e => {
            resolve('');
          }
        })
      ));
    });
  }

  public async isAppuserDeviceList(user: string, s_ins: number): Promise<Device[]> {
    const paramsRequest: AppUserDeviceListRequest = new AppUserDeviceListRequest(user, s_ins);
    let devices: Device[] = [];
    return new Promise(async (resolve, reject) => {
      await firstValueFrom((await this.sicorAPI.isAppuserDeviceListRequest(paramsRequest)).pipe(
        take(1),
        tap({
          next: async res => {
            if (res.resultSet != null && res.resultSet[0] != null) {
              if (res.resultSet[0].error != null && res.resultSet[0].error === 401) {
                // this.modalCtrl.dismiss(false, '', 'loadSpinner');
                resolve(undefined);
                await this.navigation.logout();
                await this.gms.openGenericModal(await this.translate.getTranslate('MODAL.titleError'), await this.translate.getTranslate('MESSAGES.resourceNotAvailable'),
                  'modal-default', await this.translate.getTranslate('BUTTON.agreed'));
              }
              else {
                if (res.resultSet[0].error != 0) {
                  devices = res.resultSet;
                  for (let i = 0; i < res.resultSet.length; i++) {
                    if (devices[i].last_photo_type === 'VIDEO') {
                      if (devices[i].last_photo.indexOf('http://') !== -1 || devices[i].last_photo.indexOf('https://') !== -1) {
                        devices[i].last_photo = devices[i].last_photo.toLowerCase() + '#t=0.5';
                      }
                      else {
                        let urlRec = devices[i].last_photo;
                        devices[i].last_photo = `data:video/mp4;base64,${urlRec}`
                      }
                    }
                    else {
                      if (devices[i].last_photo.indexOf('http://') !== -1 || devices[i].last_photo.indexOf('https://') !== -1) {
                        devices[i].last_photo = devices[i].last_photo.toLowerCase();
                      }
                      else {
                        if (devices[i].last_photo != '') {
                          let urlRec = devices[i].last_photo;
                          devices[i].last_photo = `data:image/jpg;base64,${urlRec}`;
                        }
                      }
                    }
                  }
                }
                resolve(devices);
              }
            } else {
              resolve(devices);
            }
          }, error: e => {
            resolve(devices);
          }
        })
      ));
    });
  }

  public async changeZoneStatus(requestParams: CommandConnectorQueueInsRequest) {
    return new Promise(async (resolve, reject) => {
      await firstValueFrom((await this.sicorAPI.cAppuserCommandConnectorQueueInsRequest(requestParams)).pipe(
        take(1),
        tap({
          next: async res => {
            if (res.resultSet != null && res.resultSet[0] != null && res.resultSet[0].error != null) {
              if (res.resultSet[0].error === 401) {
                resolve(undefined);
                await this.navigation.logout();
                await this.gms.openGenericModal(await this.translate.getTranslate('MODAL.titleError'), await this.translate.getTranslate('MESSAGES.resourceNotAvailable'),
                  'modal-default', await this.translate.getTranslate('BUTTON.agreed'));
              }
              else if (res.resultSet[0].error === 0) {
                resolve(true);
              }
              else {
                resolve(false);
              }
            }
            else {
              resolve(false);
            }
          }, error: e => {
            resolve(false);
          }
        })
      ));
    });
  }

  public async changeZoneName(requestParams: AppuserZoneUpdRequest) {
    return new Promise(async (resolve, reject) => {
      await firstValueFrom((await this.sicorAPI.cAppuserZoneUpdRequest(requestParams)).pipe(
        take(1),
        tap({
          next: async res => {
            if (res.returnCode != null) {
              if (res.returnCode === 401) {
                resolve(undefined);
                await this.navigation.logout();
                await this.gms.openGenericModal(await this.translate.getTranslate('MODAL.titleError'), await this.translate.getTranslate('MESSAGES.resourceNotAvailable'),
                  'modal-default', await this.translate.getTranslate('BUTTON.agreed'));
              }
              else if (res.returnCode === 0) {
                resolve(true);
              }
              else {
                resolve(false);
              }
            }
            else {
              resolve(false);
            }
          }, error: e => {
            resolve(false);
          }
        })
      ));
    });
  }

  public async changeInstallationName(requestParams: InstallationNameChangeRequest) {
    return new Promise(async (resolve, reject) => {
      await firstValueFrom((await this.sicorAPI.cAppuserAliasNameUpd(requestParams)).pipe(
        take(1),
        tap({
          next: async res => {
            if (res.returnCode != null) {
              if (res.returnCode === 401) {
                resolve(undefined);
                await this.navigation.logout();
                await this.gms.openGenericModal(await this.translate.getTranslate('MODAL.titleError'), await this.translate.getTranslate('MESSAGES.resourceNotAvailable'),
                  'modal-default', await this.translate.getTranslate('BUTTON.agreed'));
              }
              else if (res.returnCode === 0) {
                resolve(true);
              }
              else {
                resolve(false);
              }
            }
            else {
              resolve(false);
            }
          }, error: e => {
            resolve(false);
          }
        })
      ));
    });
  }

  async checkIfUserIsMaster() {
    let currentInstallation = this.gvs.currentInstallation;
    let user = this.gvs.user.userName;
    let masterUsersList = await this.getContactsUser(user, currentInstallation.s_ins, 0);
    if (masterUsersList != null && masterUsersList.length > 0) {
      masterUsersList = masterUsersList.filter(contact => (contact.ilevel === 2));
      this.gvs.isCurrentUserMaster = masterUsersList.find(x => x.email1.toLowerCase() === user.toLowerCase() && x.ilevel === 2) ? true : false;
    }
  }



}
